import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
    MoreHorizontal,
    ArrowUpDown,
    Pencil,
    Trash2,
    FileText,
    ExternalLink,
} from "lucide-react";
import { useDialog } from "@/hooks/useDialog";
import { useDeleteChapter, useDeleteChapters, useUpdateChapter } from "@/hooks/useChapters";
import { Chapter } from "@/services/supabase/chapters";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ChaptersTableProps {
    chapters: Partial<Chapter>[];
    storyId?: string;
    projectId?: string;
    isLoading?: boolean;
}

type SortConfig = {
    key: keyof Chapter | 'word_count' | 'last_saved_at';
    direction: 'asc' | 'desc';
};

export function ChaptersTable({ chapters, storyId: storyIdProp, projectId: projectIdProp, isLoading }: ChaptersTableProps) {
    const navigate = useNavigate();
    const params = useParams();
    const projectId = projectIdProp || params.projectId;
    const storyId = storyIdProp || params.storyId;

    // State
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'display_order', direction: 'asc' });
    const [chapterToDelete, setChapterToDelete] = useState<number | null>(null);
    const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

    // Hooks
    const { openDialog } = useDialog();
    const deleteChapterMutation = useDeleteChapter();
    const deleteChaptersMutation = useDeleteChapters();
    const updateChapterMutation = useUpdateChapter();

    // Derived State
    const sortedChapters = useMemo(() => {
        const sorted = [...chapters];
        sorted.sort((a, b) => {
            const aValue = a[sortConfig.key as keyof Chapter] || '';
            const bValue = b[sortConfig.key as keyof Chapter] || '';

            // Handle dates
            if (sortConfig.key === 'last_saved_at' || sortConfig.key === 'created_at') {
                const dateA = new Date(a[sortConfig.key] as string || 0).getTime();
                const dateB = new Date(b[sortConfig.key] as string || 0).getTime();
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [chapters, sortConfig]);

    // Handlers
    const handleSort = (key: SortConfig['key']) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedIds(chapters.map(c => c.id!).filter(Boolean));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
        }
    };

    const handleEditChapter = (chapter: Partial<Chapter>) => {
        if (!projectId) return;

        openDialog('chapter', {
            defaultValues: {
                name: chapter.title,
                description: chapter.description || '',
                outline: ''
            },
            isEditing: true,
            chapterId: chapter.id
        }, async (data: { name: string; description: string }) => {
            try {
                await updateChapterMutation.mutateAsync({
                    chapterId: chapter.id!,
                    title: data.name,
                    description: data.description,
                    projectId: projectId
                });
                toast.success('Chapter updated successfully');
            } catch (error) {
                console.error('Error updating chapter:', error);
                toast.error('Failed to update chapter');
            }
        });
    };

    const confirmDelete = async () => {
        if (!chapterToDelete || !projectId || !storyId) return;

        try {
            await deleteChapterMutation.mutateAsync({
                chapterId: chapterToDelete,
                storyId,
                projectId
            });
            toast.success('Chapter deleted');
            setChapterToDelete(null);
        } catch (error) {
            console.error('Error deleting chapter:', error);
            toast.error('Failed to delete chapter');
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.length === 0 || !projectId || !storyId) return;

        try {
            await deleteChaptersMutation.mutateAsync({
                chapterIds: selectedIds,
                storyId,
                projectId
            });
            toast.success(`${selectedIds.length} chapters deleted`);
            setSelectedIds([]);
            setIsBulkDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting chapters:', error);
            toast.error('Failed to delete chapters');
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center text-muted-foreground">Loading chapters...</div>;
    }

    if (chapters.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground/50 border border-dashed border-border/50 rounded-xl bg-card/50">
                <FileText className="w-10 h-10 mb-4 opacity-20" />
                <p>No chapters yet. Create your first chapter!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Bulk Actions Toolbar */}
            {selectedIds.length > 0 && (
                <div className="flex items-center justify-between p-2 px-4 bg-muted/50 rounded-lg border border-border/50 animate-in fade-in slide-in-from-top-2">
                    <span className="text-sm text-muted-foreground font-medium">
                        {selectedIds.length} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="destructive"
                            className="h-8 gap-2"
                            onClick={() => setIsBulkDeleteDialogOpen(true)}
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Selected
                        </Button>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-transparent bg-muted/30">
                            <TableHead className="w-[40px] px-4">
                                <Checkbox
                                    checked={selectedIds.length === chapters.length && chapters.length > 0}
                                    onCheckedChange={handleSelectAll}
                                    aria-label="Select all"
                                    className={cn(
                                        "transition-opacity duration-200",
                                        selectedIds.length > 0 ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                    )}
                                />
                            </TableHead>
                            <TableHead className="w-[60px] text-center">#</TableHead>
                            <TableHead className="cursor-pointer" onClick={() => handleSort('title')}>
                                <div className="flex items-center gap-1">
                                    Title
                                    <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                                </div>
                            </TableHead>
                            <TableHead className="w-[120px]">Words</TableHead>
                            <TableHead
                                className="w-[150px] cursor-pointer"
                                onClick={() => handleSort('last_saved_at')}
                            >
                                <div className="flex items-center gap-1">
                                    Last Updated
                                    <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                                </div>
                            </TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedChapters.map((chapter, index) => (
                            <TableRow
                                key={chapter.id}
                                className={cn(
                                    "group cursor-pointer hover:bg-muted/30 transition-colors",
                                    selectedIds.includes(chapter.id!) && "bg-muted/50"
                                )}
                                onClick={() => navigate(`/projects/${projectId}/chapters/${chapter.id}`)}
                            >
                                <TableCell className="px-4" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={selectedIds.includes(chapter.id!)}
                                        onCheckedChange={(checked) => handleSelectRow(chapter.id!, checked as boolean)}
                                        aria-label={`Select ${chapter.title}`}
                                        className={cn(
                                            "transition-opacity duration-200",
                                            selectedIds.includes(chapter.id!) || selectedIds.length > 0
                                                ? "opacity-100"
                                                : "opacity-0 group-hover:opacity-100"
                                        )}
                                    />
                                </TableCell>
                                <TableCell className="text-center font-mono text-muted-foreground/70 text-xs text-muted-foreground/30 group-hover:text-muted-foreground/70 transition-colors">
                                    {index + 1}
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                                            {chapter.title}
                                        </span>
                                        {chapter.description && (
                                            <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                                                {chapter.description}
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge variant="secondary" className="font-mono text-[10px] font-normal opacity-70">
                                        {(chapter as any).word_count || 0} words
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                    {chapter.last_saved_at
                                        ? formatDistanceToNow(new Date(chapter.last_saved_at), { addSuffix: true })
                                        : formatDistanceToNow(new Date(chapter.created_at!), { addSuffix: true })
                                    }
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                                                <span className="sr-only">Open menu</span>
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-[160px]">
                                            <DropdownMenuItem onClick={() => navigate(`/projects/${projectId}/chapters/${chapter.id}`)}>
                                                <ExternalLink className="mr-2 h-4 w-4" />
                                                Open Editor
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleEditChapter(chapter)}>
                                                <Pencil className="mr-2 h-4 w-4" />
                                                Edit Details
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                className="text-destructive focus:text-destructive"
                                                onClick={() => setChapterToDelete(chapter.id!)}
                                            >
                                                <Trash2 className="mr-2 h-4 w-4" />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Individual Delete Dialog */}
            <AlertDialog open={!!chapterToDelete} onOpenChange={(open) => !open && setChapterToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the chapter and remove your data from our servers.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Bulk Delete Dialog */}
            <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {selectedIds.length} chapters?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the selected chapters.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete {selectedIds.length} Chapters
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
